import React, {useEffect, useMemo, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers'
import {ProfileImg} from '../../../../Utils/fileUrlHelper'
import {InterpreterNameWithCaseCount} from './InterpreterNameWithCaseCount'
import {InterpreterScoreBadge} from './InterpreterScoreBadge'
import {
  getActiveNonRecommendedAssignmentReasons,
  NonRecommendedAssignmentReason,
} from './nonRecommendedAssignmentReasonApi'

const ACCENT = '#a3a242'
const DANGER = '#d7383c'

type SuboptimalAssignmentModalProps = {
  recommendedInterpreters: any[]
  selectedInterpreter: any
  onClose: () => void
  onAssignRecommended: (interpreter: any) => void
  onConfirmOriginallySelected: (
    reasonCode: number,
    reason: string,
    reasonDetail?: string
  ) => void
}

const getInitials = (name: string) => {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return ''
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
  }
  return trimmed.slice(0, 2).toUpperCase()
}

const InterpreterAvatar: React.FC<{interpreter: any}> = ({interpreter}) => {
  if (interpreter?.profileImage) {
    return (
      <div className='symbol symbol-45px rounded-circle flex-shrink-0'>
        <ProfileImg imagePath={interpreter.profileImage} alt='' className='rounded-circle' />
      </div>
    )
  }

  return (
    <div
      className='rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center fw-semibold'
      style={{
        width: 42,
        height: 42,
        backgroundColor: '#e8fff3',
        color: '#60bb1a',
        fontSize: '1rem',
      }}
    >
      {getInitials(interpreter?.name ?? '')}
    </div>
  )
}

const InterpreterCardRow: React.FC<{
  interpreter: any
  showAssign?: boolean
  onAssign?: () => void
}> = ({interpreter, showAssign, onAssign}) => (
  <div
    className='d-flex align-items-center'
    style={{
      gap: 14,
      border: showAssign ? '1px solid rgba(163,162,66,0.35)' : undefined,
      background: showAssign ? '#fdfdf7' : undefined,
      borderRadius: showAssign ? 10 : undefined,
      padding: showAssign ? '12px 16px' : undefined,
    }}
  >
    <InterpreterAvatar interpreter={interpreter} />
    <div className='min-w-0' style={{width: 180}}>
      <div className='fw-bold text-gray-900' style={{fontSize: '1rem'}}>
        <InterpreterNameWithCaseCount
          name={interpreter?.name ?? ''}
          accountCompletedCaseCount={interpreter?.accountCompletedCaseCount}
        />
      </div>
    </div>
    <div className='flex-grow-1 d-flex align-items-center' style={{minWidth: 0}}>
      <InterpreterScoreBadge
        score={interpreter?.recommendationScore}
        breakdown={interpreter?.scoreBreakdown}
        isNew={interpreter?.isNewInterpreter}
        placement='left'
      />
    </div>
    {showAssign && (
      <button
        type='button'
        className='btn btn-sm fw-semibold text-white flex-shrink-0'
        style={{
          backgroundColor: ACCENT,
          borderColor: ACCENT,
          borderRadius: 8,
          padding: '9px 24px',
          minWidth: 88,
        }}
        onClick={onAssign}
      >
        Assign
      </button>
    )}
  </div>
)

export const SuboptimalAssignmentModal: React.FC<SuboptimalAssignmentModalProps> = ({
  recommendedInterpreters,
  selectedInterpreter,
  onClose,
  onAssignRecommended,
  onConfirmOriginallySelected,
}) => {
  const [reasons, setReasons] = useState<NonRecommendedAssignmentReason[]>([])
  const [reasonsLoading, setReasonsLoading] = useState(true)
  const [selectedReasonCode, setSelectedReasonCode] = useState('')
  const [otherReasonDetail, setOtherReasonDetail] = useState('')

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const loadReasons = async () => {
      setReasonsLoading(true)
      try {
        const data = await getActiveNonRecommendedAssignmentReasons()
        if (!cancelled) {
          setReasons(data)
        }
      } catch {
        if (!cancelled) {
          setReasons([])
        }
      } finally {
        if (!cancelled) {
          setReasonsLoading(false)
        }
      }
    }
    loadReasons()
    return () => {
      cancelled = true
    }
  }, [])

  const selectedReason = useMemo(
    () => reasons.find((r) => String(r.code) === selectedReasonCode),
    [reasons, selectedReasonCode]
  )

  const canConfirm = useMemo(() => {
    if (!selectedReason) return false
    if (selectedReason.requiresDetail) {
      return otherReasonDetail.trim().length > 0
    }
    return true
  }, [selectedReason, otherReasonDetail])

  const handleConfirm = () => {
    if (!canConfirm || !selectedReason) return
    onConfirmOriginallySelected(
      selectedReason.code,
      selectedReason.name,
      selectedReason.requiresDetail ? otherReasonDetail.trim() : undefined
    )
  }

  return (
    <>
      <div className='modal fade show d-block' tabIndex={-1} role='dialog' aria-modal='true'>
        <div className='modal-dialog modal-dialog-centered' style={{maxWidth: 640}}>
          <div className='modal-content border-0' style={{borderRadius: 16, overflow: 'hidden'}}>
            <div
              className='d-flex align-items-start'
              style={{gap: 14, padding: '22px 24px 18px', borderBottom: '1px solid #f1f1f2'}}
            >
              <div
                className='d-flex align-items-start justify-content-center flex-shrink-0'
                style={{
                  width: 40,
                  height: 56,
                  borderRadius: 999,
                  backgroundColor: '#fff5f8',
                  color: DANGER,
                  paddingTop: 9,
                }}
              >
                <i className='bi bi-exclamation-circle' style={{fontSize: 20}} />
              </div>
              <div className='flex-grow-1 min-w-0'>
                <h4 className='fw-bold mb-2' style={{fontSize: '1.35rem', color: '#181c32'}}>
                  Suboptimal Assignment
                </h4>
                <p className='mb-1' style={{color: '#5e6278', fontSize: '0.98rem', lineHeight: 1.5}}>
                  The selected interpreter is not highly recommended for this assignment. Consider
                  one of our top recommendations as it&apos;s based on important factors.
                </p>
                <p
                  className='mb-0 fst-italic fw-semibold'
                  style={{color: '#5e6278', fontSize: '0.95rem'}}
                >
                  Hover on the score to understand the scoring mechanism
                </p>
              </div>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-primary flex-shrink-0'
                aria-label='Close'
                onClick={onClose}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
              </button>
            </div>

            <div style={{padding: '20px 24px 24px'}}>
              <div className='d-flex align-items-center mb-3' style={{gap: 8}}>
                <i className='bi bi-check-circle' style={{color: ACCENT, fontSize: 15}} />
                <span
                  className='text-uppercase fw-bold'
                  style={{fontSize: '0.85rem', letterSpacing: '0.06em', color: '#3f4254'}}
                >
                  Top Recommended Alternatives
                </span>
              </div>

              <div className='d-flex flex-column mb-5' style={{gap: 10}}>
                {recommendedInterpreters.map((interpreter) => (
                  <InterpreterCardRow
                    key={interpreter?.fK_Interpreter ?? interpreter?.code}
                    interpreter={interpreter}
                    showAssign
                    onAssign={() => onAssignRecommended(interpreter)}
                  />
                ))}
              </div>

              <div style={{borderBottom: '1px solid #f1f1f2', marginBottom: 18}} />

              <div className='mb-1'>
                <span
                  className='text-uppercase fw-bold'
                  style={{fontSize: '0.85rem', letterSpacing: '0.06em', color: '#3f4254'}}
                >
                  Originally Selected
                </span>
              </div>
              <p
                className='fst-italic mb-3'
                style={{color: DANGER, fontSize: '0.85rem', margin: '0 0 10px'}}
              >
                To continue with the current assignment please share your feedback
              </p>

              <div
                style={{
                  border: '1px solid #e4e6ef',
                  borderRadius: 10,
                  padding: '14px 16px',
                }}
              >
                <div
                  style={{
                    paddingBottom: 12,
                    borderBottom: '1px solid #f1f1f2',
                    marginBottom: 14,
                  }}
                >
                  <InterpreterCardRow interpreter={selectedInterpreter} />
                </div>

                <label
                  className='d-block fw-semibold mb-2'
                  style={{fontSize: '0.95rem', color: '#3f4254'}}
                >
                  Reason for choosing non-recommended interpreter{' '}
                  <span style={{color: DANGER}}>*</span>
                </label>
                <select
                  className='form-select'
                  value={selectedReasonCode}
                  disabled={reasonsLoading || reasons.length === 0}
                  style={{
                    borderRadius: 8,
                    borderColor: '#e4e6ef',
                    fontSize: '0.95rem',
                    color: '#181c32',
                  }}
                  onChange={(e) => {
                    const nextCode = e.target.value
                    setSelectedReasonCode(nextCode)
                    const nextReason = reasons.find((r) => String(r.code) === nextCode)
                    if (!nextReason?.requiresDetail) {
                      setOtherReasonDetail('')
                    }
                  }}
                >
                  <option value=''>
                    {reasonsLoading ? 'Loading reasons...' : 'Select a reason...'}
                  </option>
                  {reasons.map((reason) => (
                    <option key={reason.code} value={reason.code}>
                      {reason.name}
                    </option>
                  ))}
                </select>

                {selectedReason?.requiresDetail && (
                  <textarea
                    className='form-control mt-3'
                    rows={3}
                    placeholder='Please describe the reason...'
                    value={otherReasonDetail}
                    style={{
                      borderRadius: 8,
                      borderColor: '#e4e6ef',
                      fontSize: '0.95rem',
                      color: '#181c32',
                    }}
                    onChange={(e) => setOtherReasonDetail(e.target.value)}
                  />
                )}
              </div>

              <div className='d-flex justify-content-end mt-5'>
                {!canConfirm ? (
                  <OverlayTrigger
                    placement='top'
                    overlay={
                      <Tooltip id='suboptimal-confirm-tooltip'>
                        Please add reason to continue
                      </Tooltip>
                    }
                  >
                    <span className='d-inline-block'>
                      <button
                        type='button'
                        className='btn fw-semibold'
                        disabled
                        style={{
                          backgroundColor: '#e4e6ef',
                          borderColor: '#e4e6ef',
                          color: '#a1a5b7',
                          borderRadius: 8,
                          padding: '12px 24px',
                          fontSize: '0.98rem',
                          pointerEvents: 'none',
                        }}
                      >
                        Confirm Originally Selected
                      </button>
                    </span>
                  </OverlayTrigger>
                ) : (
                  <button
                    type='button'
                    className='btn fw-semibold text-white'
                    style={{
                      backgroundColor: ACCENT,
                      borderColor: ACCENT,
                      borderRadius: 8,
                      padding: '12px 24px',
                      fontSize: '0.98rem',
                    }}
                    onClick={handleConfirm}
                  >
                    Confirm Originally Selected
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' />
    </>
  )
}
